// Per-repo run lane. Renders one repo's run inside a multi-repo ticket
// group: repo header + status + PR link, the phase timeline (reused from the
// single-run detail rendering), and a per-repo deploy action driven by that
// repo's own deployWorkflows. A single-repo ticket renders exactly one lane
// so it looks ~like the pre-multi-repo run detail.

import { useState } from "react";
import api from "../../api";
import DeployModal from "./DeployModal";
import { ReleaseCutPanel } from "./ReleaseCutPanel";
import Icon from "./icons";
import {
  Html,
  Markdown,
  PhaseGlyph,
  Spinner,
  StatusBadge,
  apiErrorMessage,
  formatDuration,
  formatMoney,
  isLive,
  phaseStatusClass,
  phaseStripColor,
  PHASE_COLOR,
  PHASE_ICON
} from "./shared";

const BODY_TAB_LABEL = { Reviewer: "Review notes", Planner: "Plan" };
const bodyTabLabel = (name) => BODY_TAB_LABEL[name] || "Output";

const PhaseStatusBadge = ({ status }) => {
  if (status === "running")
    return (
      <span
        className="bss-badge bss-badge--pink bss-badge--pulsing"
        style={{ marginLeft: 6 }}
      >
        <span className="bss-dot" />
        Running
      </span>
    );
  if (status === "failed")
    return (
      <span className="bss-badge bss-badge--red" style={{ marginLeft: 6 }}>
        <span className="bss-dot" />
        Failed
      </span>
    );
  return null;
};

const PhaseBody = ({ phase, tab, hasBody }) => {
  if (tab !== "body" || !hasBody) return null;
  if (phase.body.kind === "error")
    return (
      <div
        className="bss-error-block"
        style={{ margin: 0, border: 0, borderRadius: 0 }}
      >
        <div className="bss-hd">
          <Icon.AlertTriangle size={13} />
          Phase terminated
        </div>
        {phase.body.content}
      </div>
    );
  // HTML bodies are server-sanitized; Html re-sanitizes as defense in depth.
  if (phase.body.kind === "html") return <Html src={phase.body.content} />;
  // Safe fallback for a 'markdown'-kind body.
  return <Markdown src={phase.body.content} />;
};

function laneBorderColor(live, status) {
  if (live) return "var(--bss-boom-pink)";
  if (status === "FAILED") return "rgba(255, 82, 71, 0.32)";
  return "var(--bss-border)";
}

function deployDisabledReason(envCount, branch) {
  if (envCount === 0) return "No deploy targets for this repo";
  if (!branch) return "No PR branch yet";
  return undefined;
}

const PhaseRow = ({ phase }) => {
  const hasBody = !!(phase.body && phase.body.content);
  const expandable = hasBody;
  const [open, setOpen] = useState(false);
  const PhaseIcon = PHASE_ICON[phase.name] || Icon.Code;
  const pending = phase.status === "pending";

  return (
    <div className={`bss-phase ${phaseStatusClass(phase.status)}`}>
      <div className="bss-phase-dot">
        <PhaseGlyph status={phase.status} />
      </div>
      <div
        className={`bss-phase-row ${open ? "is-open" : ""}`.trim()}
        onClick={() => expandable && setOpen(!open)}
      >
        <span
          className="bss-phase-name"
          style={{
            color: pending ? "var(--bss-fg-muted)" : "var(--bss-fg-strong)"
          }}
        >
          {expandable && <Icon.ChevronRight size={12} className="bss-caret" />}
          <PhaseIcon size={14} style={{ color: PHASE_COLOR[phase.name] }} />
          {phase.name}
          <PhaseStatusBadge status={phase.status} />
          {phase.summary && phase.status !== "running" && (
            <span
              style={{
                fontWeight: 400,
                color: "var(--bss-fg-muted)",
                fontSize: 12.5,
                marginLeft: 6
              }}
            >
              · {phase.summary}
            </span>
          )}
        </span>
        <span className="bss-phase-meta">
          {phase.durationSec != null && (
            <span>
              <Icon.Clock
                size={11}
                style={{
                  verticalAlign: -2,
                  marginRight: 4,
                  color: "var(--bss-fg-subtle)"
                }}
              />
              {formatDuration(phase.durationSec)}
            </span>
          )}
          {phase.costUsd != null && (
            <span className="bss-cost-line">{formatMoney(phase.costUsd)}</span>
          )}
        </span>
      </div>

      {open && expandable && (
        <div className="bss-phase-body">
          <div className="bss-phase-body-tabs">
            <button type="button" className="bss-phase-tab is-active">
              {bodyTabLabel(phase.name)}
            </button>
          </div>
          <PhaseBody phase={phase} tab="body" hasBody={hasBody} />
        </div>
      )}
    </div>
  );
};

const PhaseStrip = ({ phases }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 14,
      flexWrap: "wrap"
    }}
  >
    {phases.map((p, i) => {
      const c = phaseStripColor(p.status);
      const pending = p.status === "pending";
      return (
        <span
          key={p.name || i}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 22,
            padding: "0 9px",
            borderRadius: 999,
            background: pending
              ? "transparent"
              : `color-mix(in oklab, ${c} 16%, transparent)`,
            border: `1px solid ${
              pending ? "var(--bss-border)" : "transparent"
            }`,
            fontSize: 11.5,
            fontWeight: 500,
            color: pending ? "var(--bss-fg-subtle)" : c
          }}
        >
          {p.status === "done" && <Icon.Check size={11} />}
          {p.status === "running" && <Spinner size={9} />}
          {p.status === "failed" && <Icon.X size={11} />}
          {p.name}
        </span>
      );
    })}
  </div>
);

const LaneBody = ({ phases, live, status, run, hasEnvs }) => (
  <div style={{ padding: "16px 20px 18px" }}>
    {!hasEnvs && (
      <div
        style={{
          fontSize: 11.5,
          color: "var(--bss-fg-subtle)",
          marginBottom: 12
        }}
      >
        No deploy targets for this repo.
      </div>
    )}
    {phases.length === 0 ? (
      <div style={{ color: "var(--bss-fg-muted)", fontSize: 13 }}>
        {live ? (
          <>
            <Spinner size={9} /> Run in progress — no phases recorded yet.
          </>
        ) : (
          "No phases recorded for this repo's run."
        )}
      </div>
    ) : (
      <>
        <PhaseStrip phases={phases} />
        <div className="bss-phases">
          {phases.map((p, i) => (
            <PhaseRow key={p.name || i} phase={p} />
          ))}
        </div>
      </>
    )}

    {status === "FAILED" && run && run.error && (
      <div className="bss-error-block" style={{ marginTop: 14 }}>
        <div className="bss-hd">
          <Icon.AlertTriangle size={13} /> Run failed
        </div>
        {run.error}
      </div>
    )}
  </div>
);

// Per-lane footer listing refresh + address-comments rounds that happened
// AFTER the original implement run. Empty → renders nothing; hidden when the
// lane has no PR yet. One row per run, with mode-coloured chip + status +
// relative timestamp + cost + deep-link to the run detail.
const RelatedRunsSection = ({ runs, onNavigateRun }) => {
  if (!runs || runs.length === 0) return null;
  const counts = runs.reduce((acc, r) => {
    acc[r.mode] = (acc[r.mode] || 0) + 1;
    return acc;
  }, {});
  const summary = Object.entries(counts)
    .map(([mode, n]) => `${mode} × ${n}`)
    .join(", ");
  return (
    <div
      style={{
        padding: "10px 20px 14px",
        borderTop: "1px solid var(--bss-border)",
        background: "var(--bss-bg-elev-1, transparent)"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          fontSize: 11.5,
          color: "var(--bss-fg-subtle)",
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: 0.4
        }}
      >
        <Icon.History size={11} />
        Rounds since implement
        <span
          style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}
        >
          — {summary}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {runs.map((r) => {
          const isComments = r.mode === "address-comments";
          const ChipIcon = isComments ? Icon.MessageSquare : Icon.GitBranch;
          return (
            <div
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 12,
                padding: "4px 6px",
                borderRadius: 6,
                background: "var(--bss-bg-elev-2, transparent)"
              }}
            >
              <span
                title={r.mode}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  color: "var(--bss-info)",
                  fontWeight: 500,
                  minWidth: 130
                }}
              >
                <ChipIcon size={12} />
                {r.mode}
              </span>
              <StatusBadge status={r.status} />
              <span
                style={{
                  color: "var(--bss-fg-muted)",
                  fontSize: 11.5,
                  minWidth: 80
                }}
                title={r.startedAt}
              >
                {relativeTimeLite(r.startedAt)}
              </span>
              {r.costUsd != null && (
                <span style={{ color: "var(--bss-fg-muted)", fontSize: 11.5 }}>
                  {formatMoney(r.costUsd)}
                </span>
              )}
              {r.errorPreview && (
                <span
                  style={{
                    color: "var(--bss-danger)",
                    fontSize: 11.5,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                  title={r.errorPreview}
                >
                  {r.errorPreview}
                </span>
              )}
              <span style={{ marginLeft: "auto" }}>
                {onNavigateRun && (
                  <button
                    type="button"
                    className="bss-btn bss-btn--ghost bss-btn--sm"
                    onClick={() => onNavigateRun(r.id)}
                  >
                    <Icon.ExternalLink size={11} /> Detail
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Compact relative-time formatter local to the related-runs row so we don't
// depend on the shared `relativeTime` (which returns "—" for falsy input —
// fine for cells, but we want a tighter single-line shape here).
function relativeTimeLite(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  if (ms < 60_000) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

// `run` is the per-repo run object. `repoId`/`githubRepo` describe the lane.
// `deployWorkflows` is this repo's own env list (may be empty ⇒ no deploy
// target for the repo). `relatedRuns` is the lane's refresh + address-comments
// history surfaced as a small footer. All fields are guarded for absence.
export default function RunLane({
  repoId,
  githubRepo,
  run,
  prUrl,
  status,
  deployWorkflows,
  relatedRuns,
  ticketKey,
  onNavigateRun,
  onDeployed,
  onForceReview,
  hideDeploy
}) {
  const [deployOpen, setDeployOpen] = useState(false);
  // Force-review state — local to the lane so a click on lane A doesn't
  // disable lane B's button while in flight.
  const [forcing, setForcing] = useState(false);
  const laneStatus = (run && run.status) || status;
  const live = isLive(laneStatus);
  const phases = (run && run.phases) || [];
  const branch = run && run.branch;
  const pr = (run && run.pr) || (prUrl ? { url: prUrl } : null);
  const envs = deployWorkflows || [];
  const canDeploy = envs.length > 0 && !!branch;
  // Force-review is operator escape hatch for a missed/lost webhook. Needs
  // a PR number to dispatch; absent ⇒ nothing to force-review (the agent
  // is still implementing or the PR hasn't opened yet).
  const canForceReview =
    !!repoId && !!pr && typeof pr.number === "number" && !forcing;
  const handleForceReview = () => {
    if (!canForceReview) return;
    setForcing(true);
    api
      .bssForceReview({ repoId, prNumber: pr.number })
      .then(() => {
        if (onForceReview)
          onForceReview({ repoId, prNumber: pr.number, ok: true });
      })
      .catch((err) => {
        if (onForceReview) {
          onForceReview({
            repoId,
            prNumber: pr.number,
            ok: false,
            error: apiErrorMessage(err, "Force-review failed")
          });
        }
      })
      .finally(() => setForcing(false));
  };

  return (
    <div
      className="bss-card"
      style={{
        padding: 0,
        marginBottom: 16,
        overflow: "hidden",
        borderColor: laneBorderColor(live, laneStatus)
      }}
    >
      <div
        className="bss-card-hd"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap"
        }}
      >
        <Icon.Github size={14} style={{ color: "var(--bss-fg-muted)" }} />
        <span
          style={{
            fontFamily: "var(--bss-font-mono)",
            fontSize: 13,
            color: "var(--bss-fg-strong)",
            fontWeight: 500
          }}
        >
          {githubRepo || repoId || "repo"}
        </span>
        <StatusBadge status={laneStatus} pulsing={live} />
        {branch && (
          <code className="bss-code-inline" style={{ fontSize: 11 }}>
            {branch}
          </code>
        )}
        <span
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}
        >
          {pr && (pr.url || pr.number != null) && (
            <a
              className="bss-badge bss-badge--blue"
              href={pr.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon.GitPullRequest size={11} />{" "}
              {pr.number != null ? `#${pr.number}` : "PR"}
            </a>
          )}
          {run && run.id && onNavigateRun && (
            <button
              type="button"
              className="bss-btn bss-btn--ghost bss-btn--sm"
              onClick={() => onNavigateRun(run.id)}
            >
              <Icon.ExternalLink size={12} /> Run detail
            </button>
          )}
          <button
            type="button"
            className="bss-btn bss-btn--ghost bss-btn--sm"
            disabled={!canForceReview}
            title={
              !pr || typeof pr.number !== "number"
                ? "Need a PR number to force-review"
                : "Force a BSS review on this PR — bypasses webhook + dedup. Use when a label-add didn't trigger a review (e.g. lost webhook during deploy)."
            }
            onClick={handleForceReview}
          >
            {forcing ? (
              <>
                <Spinner size={11} /> Forcing…
              </>
            ) : (
              <>
                <Icon.Eye size={12} /> Force review
              </>
            )}
          </button>
          {!hideDeploy && (
            <button
              type="button"
              className="bss-btn bss-btn--ghost bss-btn--sm"
              disabled={!canDeploy}
              title={deployDisabledReason(envs.length, branch)}
              onClick={() => canDeploy && setDeployOpen(true)}
            >
              <Icon.PlayCircle size={12} /> Deploy
            </button>
          )}
        </span>
      </div>

      {run && run.releaseCut ? (
        <ReleaseCutPanel releaseCut={run.releaseCut} embedded />
      ) : (
        <LaneBody
          phases={phases}
          live={live}
          status={laneStatus}
          run={run}
          hasEnvs={envs.length > 0}
        />
      )}

      <RelatedRunsSection runs={relatedRuns} onNavigateRun={onNavigateRun} />

      <DeployModal
        open={deployOpen}
        onClose={() => setDeployOpen(false)}
        ticketKey={ticketKey}
        branch={branch}
        environments={envs}
        repoId={repoId}
        onDeployed={(res, env) => {
          setDeployOpen(false);
          onDeployed && onDeployed(res, env, { repoId, githubRepo });
        }}
      />
    </div>
  );
}
